import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { sendPhoneVerificationOtp, verifyPhoneVerificationOtp } from '../../../services/api';
import CardIcon from '../icons/CardIcon';
import OtpCodeInput from '../inputs/OtpCodeInput';

const formatCountdown = (seconds) => `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;

function maskPhone(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  if (digits.length < 7) return phone;
  if (digits.startsWith('260')) return `+260 ${digits.slice(3, 6)}***${digits.slice(-3)}`;
  return `${String(phone).startsWith('+') ? '+' : ''}${digits.slice(0, 3)}***${digits.slice(-3)}`;
}

function PhoneVerification({ phone, onAddPhone, onVerified }) {
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState(Array(6).fill(''));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [expiresIn, setExpiresIn] = useState(0);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (!otpSent) return undefined;
    const timer = window.setInterval(() => {
      setExpiresIn((current) => Math.max(0, current - 1));
      setResendIn((current) => Math.max(0, current - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [otpSent]);

  const sendCode = async () => {
    setBusy(true);
    setError('');
    try {
      await sendPhoneVerificationOtp();
      setOtpSent(true);
      setOtp(Array(6).fill(''));
      setExpiresIn(300);
      setResendIn(60);
      toast.success('Verification code sent.');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async (event) => {
    event.preventDefault();
    const code = otp.join('');
    if (expiresIn <= 0) {
      setError('This code has expired. Request a new one.');
      return;
    }
    if (code.length !== 6) return;
    setBusy(true);
    setError('');
    try {
      await verifyPhoneVerificationOtp(code);
      toast.success('Phone number verified.');
      setOtpSent(false);
      setOtp(Array(6).fill(''));
      await onVerified?.();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-2xl border border-amber-200 bg-amber-50/70 p-4" aria-labelledby="phone-verification-title">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 id="phone-verification-title" className="flex items-center gap-2 text-sm font-bold text-navy"><CardIcon name="phone" className="h-4 w-4 text-orange-700" />Phone Number</h3>
          <p className="mt-1 text-sm font-semibold text-slate-800">{phone ? maskPhone(phone) : 'No phone number on profile'}</p>
          <p className="mt-1 inline-flex items-center gap-1.5 text-xs font-semibold text-amber-800"><CardIcon name="warning" className="h-3.5 w-3.5" />Phone Not Yet Verified</p>
        </div>
        {!phone ? (
          <button type="button" onClick={onAddPhone} className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-orange px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600"><CardIcon name="pencil" />Add phone</button>
        ) : !otpSent ? (
          <button type="button" onClick={sendCode} disabled={busy} className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-orange px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-60"><CardIcon name="phone" />{busy ? 'Sending...' : 'Verify'}</button>
        ) : null}
      </div>
      {otpSent && phone && (
        <form onSubmit={verifyCode} className="mt-4 space-y-3">
          <OtpCodeInput value={otp} onChange={setOtp} disabled={busy || expiresIn <= 0} />
          <p className="text-xs text-slate-600">{expiresIn > 0 ? `Code expires in ${formatCountdown(expiresIn)}` : 'Code expired. Request a new one.'}</p>
          <div className="flex flex-wrap items-center justify-between gap-3">
            {resendIn > 0 ? <p className="text-xs text-slate-500">Resend code in {resendIn}s</p> : <button type="button" onClick={sendCode} disabled={busy} className="text-sm font-semibold text-navy hover:underline disabled:opacity-50">Resend code</button>}
            <button type="submit" disabled={busy || expiresIn <= 0 || otp.join('').length !== 6} className="inline-flex items-center justify-center gap-2 rounded-xl bg-navy px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"><CardIcon name="check" />{busy ? 'Verifying...' : 'Verify code'}</button>
          </div>
        </form>
      )}
      {error && <p className="mt-3 text-sm text-rose-700" role="alert">{error}</p>}
    </section>
  );
}

export default PhoneVerification;